import React from 'react';
import { useTranslation } from 'react-i18next';
import type { FrakoblingBlokkering } from '@origo/planregister';
import { Button, Dialog } from '@origo/origo-ui';
import type { DocumentItem } from '../../../types';
import styles from './DeleteDocumentDialog.module.css';

interface DeleteDocumentDialogProps {
    pendingDelete: DocumentItem | null;
    /** Når satt: dokumentet kan ikke slettes, og dialogen forklarer hvorfor (#14900). */
    blokkering: FrakoblingBlokkering | null;
    /** Sperren kan ikke avgjøres ennå (planen lastes) — «Slett» holdes deaktivert. */
    avventerSjekk?: boolean;
    onConfirm: () => void;
    onCancel: () => void;
    isDeleting?: boolean;
}

/** Bekreft sletting av et plandokument (#14579), eller forklar hvorfor det er sperret. */
const DeleteDocumentDialog: React.FC<DeleteDocumentDialogProps> = ({
    pendingDelete,
    blokkering,
    avventerSjekk,
    onConfirm,
    onCancel,
    isDeleting,
}) => {
    const { t } = useTranslation();
    const navn = pendingDelete?.dokumentnavn ?? '';

    return (
        <Dialog
            open={pendingDelete !== null}
            onClose={onCancel}
            title={t(blokkering ? 'documents.delete.blockedTitle' : 'documents.delete.title')}
            footer={
                <div className={styles.dialogFooter}>
                    {blokkering ? (
                        <Button variant="secondary" onClick={onCancel}>
                            {t('common.close')}
                        </Button>
                    ) : (
                        <>
                            <Button variant="secondary" onClick={onCancel} disabled={isDeleting}>
                                {t('common.cancel')}
                            </Button>
                            <Button variant="danger" onClick={onConfirm} disabled={isDeleting || avventerSjekk}>
                                {t('documents.delete.confirm')}
                            </Button>
                        </>
                    )}
                </div>
            }
        >
            <p>
                {blokkering
                    ? t('documents.delete.blockedVedtattBestemmelser', { navn })
                    : t('documents.delete.body', { navn })}
            </p>
        </Dialog>
    );
};

export default DeleteDocumentDialog;
